/**
 * Build a rich meta description from restaurant data and a location name.
 * Client-side mirror of server's utils/seoDescriptionBuilder.js.
 */

function formatCuisineLabel(cuisine) {
  if (!cuisine) return null;
  const lower = cuisine.toLowerCase();
  if (lower === "vegan" || lower === "vegetarian") return null;
  return cuisine.replace(/\b\w/g, (l) => l.toUpperCase());
}

export function buildLocationMetaDescription(restaurants, locationName) {
  if (!restaurants || restaurants.length === 0 || !locationName) {
    return `Discover vegan-friendly dishes at restaurants in ${locationName || "your area"}. Find plant-based options with VegifyIt.`;
  }

  const count = restaurants.length;
  const parts = [];

  parts.push(
    `Explore ${count} restaurant${count !== 1 ? "s" : ""} with vegan options in ${locationName}.`
  );

  const veganCount = restaurants.filter((r) =>
    (r.cuisine || []).some((c) => c.toLowerCase() === "vegan")
  ).length;
  const vegetarianCount = restaurants.filter((r) =>
    (r.cuisine || []).some((c) => c.toLowerCase() === "vegetarian")
  ).length;

  if (veganCount > 0) {
    parts.push(
      `Including ${veganCount} fully-vegan restaurant${veganCount !== 1 ? "s" : ""}.`
    );
  } else if (vegetarianCount > 0) {
    parts.push(
      `Including ${vegetarianCount} fully-vegetarian restaurant${vegetarianCount !== 1 ? "s" : ""}.`
    );
  }

  // Prioritize restaurants with the most vegan options (highest leaf count)
  const byOptions = [...restaurants].sort(
    (a, b) => (b.veganOptions || []).length - (a.veganOptions || []).length
  );
  const dishMentions = [];
  for (const r of byOptions) {
    if (dishMentions.length >= 2) break;
    const options = r.veganOptions || [];
    if (options.length > 0 && r.name) {
      dishMentions.push({
        dish: options[0].option,
        restaurant: r.name,
      });
    }
  }

  if (dishMentions.length > 0) {
    const mentions = dishMentions
      .map((m) => `the ${m.dish} at ${m.restaurant}`)
      .join(", ");
    parts.push(`Try ${mentions}, and more.`);
  }

  const cuisineFreq = {};
  for (const r of restaurants) {
    for (const c of r.cuisine || []) {
      const label = formatCuisineLabel(c);
      if (label) cuisineFreq[label] = (cuisineFreq[label] || 0) + 1;
    }
  }
  const topCuisines = Object.entries(cuisineFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([label]) => label);

  if (topCuisines.length > 0) {
    parts.push(`Options include ${topCuisines.join(", ")}.`);
  }

  return parts.join(" ");
}
